/**
 * DI-2-2 §2.4 — C1 continuity / cadence (`04` §3 row C1: "intervals vs
 * nominal; a skip = one nominal interval missing"). No flight plan needed.
 *
 * The rule, exactly (every band is relative to `nominalS`, the MEDIAN of the
 * flight's consecutive intervals, so it scales with the camera's cadence):
 *
 *   interval <  1.5  × nominal             → normal (jitter)
 *   interval in [1.5,  2.5 ] × nominal     → skip        (2 × nominal ± 25 %: one frame missing)
 *   interval in (2.5,  3.75] × nominal     → double skip (3 × nominal ± 25 %: two frames missing;
 *                                            the overlap [2.25, 2.5] with the skip band goes to "skip")
 *   interval >  3.75 × nominal             → pause (informational — the pilot paused)
 *
 * Intervals at or above the flight split threshold never reach this function:
 * they split the flight (flights.ts).
 */
import type { Cadence, CadencePause, CadenceSkip } from "./types.js";

export const SKIP_TOLERANCE = 0.25;

export interface CadenceFrame {
  index: number;
  /** `capturedAtMs`. Frames must be passed in time order. */
  t: number;
}

function median(sorted: readonly number[]): number {
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  const hi = sorted[mid] ?? 0;
  if (n % 2 === 1) return hi;
  return ((sorted[mid - 1] ?? hi) + hi) / 2;
}

export function cadence(frames: readonly CadenceFrame[]): Cadence {
  // pairs[i] = the frames either side of interval i
  const pairs: [CadenceFrame, CadenceFrame][] = [];
  frames.forEach((b, i) => {
    const a = frames[i - 1];
    if (a) pairs.push([a, b]);
  });
  const intervals = pairs.map(([a, b]) => (b.t - a.t) / 1000);
  if (intervals.length === 0) {
    return { nominalS: null, intervalCount: 0, skips: [], pauses: [] };
  }
  const nominalS = median(intervals.slice().sort((x, y) => x - y));
  const skips: CadenceSkip[] = [];
  const pauses: CadencePause[] = [];
  if (nominalS <= 0) {
    // a burst with identical timestamps has no cadence to measure against
    return { nominalS, intervalCount: intervals.length, skips, pauses };
  }
  const lo1 = 2 * nominalS * (1 - SKIP_TOLERANCE);
  const hi1 = 2 * nominalS * (1 + SKIP_TOLERANCE);
  const hi2 = 3 * nominalS * (1 + SKIP_TOLERANCE);
  pairs.forEach(([from, to], i) => {
    const intervalS = (to.t - from.t) / 1000;
    const at = { fromIndex: from.index, toIndex: to.index, afterPosition: i };
    if (intervalS < lo1) return;
    if (intervalS <= hi1) {
      skips.push({ kind: "skip", missingFrames: 1, ...at, intervalS });
    } else if (intervalS <= hi2) {
      skips.push({ kind: "double_skip", missingFrames: 2, ...at, intervalS });
    } else {
      pauses.push({ ...at, intervalS });
    }
  });
  return { nominalS, intervalCount: intervals.length, skips, pauses };
}
